import { useEffect, useState } from "react";
import { Users, Eye, MonitorSmartphone, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const KEY_STORE = "kc_admin_key";

export default function AdminTraffic() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async (key = adminKey) => {
    try {
      const r = await api.get("/traffic", { headers: { "X-Admin-Key": key } });
      setData(r.data);
      setError("");
      localStorage.setItem(KEY_STORE, key);
    } catch {
      setData(null);
      if (key) setError("Invalid admin key.");
    }
  };

  useEffect(() => { if (adminKey) load(adminKey); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const maxViews = data ? Math.max(1, ...data.daily.map((d) => d.views)) : 1;
  const inputCls = "border border-[#E5E5DF] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#1A1A1A]";

  const Stat = ({ icon: Icon, label, value, sub, testid }) => (
    <div className="border border-[#E5E5DF] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#B84A3B]"><Icon size={15} /><span className="overline text-[0.6rem]">{label}</span></div>
      <div className="font-display text-4xl tracking-tight mt-2">{value}</div>
      {sub && <div className="text-xs text-[#6E6E69] font-body mt-1">{sub}</div>}
    </div>
  );

  return (
    <main className="max-w-[1000px] mx-auto px-5 sm:px-8 py-14 min-h-[70vh]" data-testid="admin-traffic-page">
      <Seo title="Traffic" path="/admin/traffic" noindex />
      <div className="overline text-[#B84A3B] mb-2">Store Admin</div>
      <h1 className="font-display text-4xl tracking-tight mb-2">Website Traffic</h1>
      <p className="text-sm text-[#6E6E69] mb-8 font-body">
        Live visits counted by your own site — no cookies, no personal data, updates in real time.
      </p>

      <label className="block mb-8 max-w-sm">
        <span className="overline text-[#1A1A1A] block mb-1.5">Admin Key</span>
        <div className="flex gap-2">
          <input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Enter admin key"
            data-testid="admin-key-input" className={`${inputCls} flex-1`} />
          <button onClick={() => load()} data-testid="admin-load-button"
            className="px-4 border border-[#1A1A1A] text-[0.65rem] font-mono tracking-[0.15em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors">Load</button>
        </div>
        {error && <p className="text-xs text-[#B84A3B] font-body mt-2" data-testid="traffic-error">{error}</p>}
      </label>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10" data-testid="traffic-stats">
            <Stat icon={Eye} label="Views today" value={data.today.views} sub={`${data.today.visitors} visitors`} testid="stat-today" />
            <Stat icon={TrendingUp} label="Views yesterday" value={data.yesterday.views} sub={`${data.yesterday.visitors} visitors`} testid="stat-yesterday" />
            <Stat icon={Users} label="Visitors · 30 days" value={data.visitors_30d} sub={`${data.total_30d} total views`} testid="stat-30d" />
            <Stat icon={MonitorSmartphone} label="Mobile share" value={`${Math.round(((data.devices.mobile || 0) / Math.max(1, data.total_30d)) * 100)}%`} sub={`${data.devices.desktop || 0} desktop views`} testid="stat-devices" />
          </div>

          <h2 className="font-display text-2xl tracking-tight mb-4">Last 30 days</h2>
          <div className="border border-[#E5E5DF] p-5 mb-10" data-testid="traffic-chart">
            <div className="flex items-end gap-[3px] h-32">
              {data.daily.map((d) => (
                <div key={d.day} className="flex-1 group relative" data-testid={`bar-${d.day}`}>
                  <div className="bg-[#1A1A1A] group-hover:bg-[#B84A3B] transition-colors w-full"
                    style={{ height: `${Math.max(3, (d.views / maxViews) * 128)}px` }} />
                  <div className="absolute -top-9 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#1A1A1A] text-white text-[0.6rem] font-mono px-2 py-1 whitespace-nowrap z-10">
                    {d.day.slice(5)} · {d.views} views
                  </div>
                </div>
              ))}
              {data.daily.length === 0 && <p className="text-sm text-[#6E6E69] font-body">No visits recorded yet — chart fills as shoppers browse.</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h2 className="font-display text-2xl tracking-tight mb-4">Top pages · 30 days</h2>
              <div className="border border-[#E5E5DF]" data-testid="top-pages">
                {data.top_pages.length === 0 && <p className="p-4 text-sm text-[#6E6E69] font-body">No data yet.</p>}
                {data.top_pages.map((p) => (
                  <div key={p.path} className="flex justify-between gap-4 px-4 py-2.5 border-b border-[#E5E5DF] last:border-0 text-sm font-body">
                    <span className="truncate">{p.path}</span>
                    <span className="font-mono text-xs text-[#6E6E69] shrink-0">{p.views}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-display text-2xl tracking-tight mb-4">Where visitors come from</h2>
              <div className="border border-[#E5E5DF]" data-testid="top-refs">
                {data.top_refs.length === 0 && <p className="p-4 text-sm text-[#6E6E69] font-body">No referrals yet — direct visits don't show here.</p>}
                {data.top_refs.map((r) => (
                  <div key={r.ref} className="flex justify-between gap-4 px-4 py-2.5 border-b border-[#E5E5DF] last:border-0 text-sm font-body">
                    <span className="truncate">{r.ref}</span>
                    <span className="font-mono text-xs text-[#6E6E69] shrink-0">{r.views}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
